import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import axios from "axios";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const API_BASE =
  "https://api.mahal.qa/api";

const RestaurantDeals = () => {

  const { t } = useTranslation();

  const location = useLocation();

  const navigate = useNavigate();

  const [deals, setDeals] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [cart, setCart] = useState({});

  const [timers, setTimers] =
    useState({});

  /* ================= AUTH CHECK ================= */

  const checkAuth = () => {

    const user =
      localStorage.getItem("customer") ||
      localStorage.getItem("user") ||
      localStorage.getItem("token");

    if (!user) {

      alert(
        "Please Register/Login First To Continue"
      );

      return false;

    }

    return true;

  };

  /* ================= FETCH PRODUCTS ================= */

  useEffect(() => {

    const fetchDeals = async () => {

      try {

        const res = await axios.get(
          `${API_BASE}/gridlist`
        );

        const products =
          res.data.products || [];

        /* ================= FILTER OFFERS ================= */

        const offers = products.filter(
          (p) =>
            p.offer_type &&
            (
              Number(
                p.discount_percentage
              ) > 0 ||
              Number(p.flat_amount) > 0 ||
              Number(p.buy_quantity) > 0
            )
        );

        /* ================= FORMAT ================= */

        const formatted = offers.map(
          (item) => {

            const expiryDate =
              item.offer_expiry_date ||
              item.expiry_date ||
              item.offer_end_date;

            let remainingSeconds = 0;

            if (expiryDate) {

              const expiry = new Date(
                `${expiryDate}T23:59:59`
              ).getTime();

              const now =
                new Date().getTime();

              remainingSeconds =
                Math.max(
                  Math.floor(
                    (expiry - now) / 1000
                  ),
                  0
                );

            }

            return {
              ...item,
              id: item.id,
              remainingSeconds,
            };

          }
        );

        /* ================= RANDOM PRODUCTS ================= */

        const shuffledDeals =
          formatted.sort(
            () => 0.5 - Math.random()
          );

        const limitedDeals =
          shuffledDeals.slice(0, 24);

        setDeals(limitedDeals);

        const timerObj = {};

        limitedDeals.forEach(
          (deal) => {

            timerObj[deal.id] =
              deal.remainingSeconds;

          }
        );

        setTimers(timerObj);

      } catch (err) {

        console.error(
          "FETCH ERROR:",
          err
        );

      } finally {

        setLoading(false);

      }

    };

    fetchDeals();

    /* ================= AUTO REFRESH EVERY 4 HOURS ================= */

    const refreshInterval =
      setInterval(() => {

        fetchDeals();

      }, 4 * 60 * 60 * 1000);

    return () =>
      clearInterval(refreshInterval);

  }, []);

  /* ================= LIVE TIMER ================= */

  useEffect(() => {

    const interval = setInterval(() => {

      setTimers((prev) => {

        const updated = { ...prev };

        Object.keys(updated).forEach(
          (id) => {

            if (updated[id] > 0) {

              updated[id] -= 1;

            }

          }
        );

        return updated;

      });

    }, 1000);

    return () =>
      clearInterval(interval);

  }, []);

  /* ================= FORMAT TIMER ================= */

  const formatTime = (seconds) => {

    if (seconds <= 0) {

      return "Offer Expired";

    }

    const days = Math.floor(
      seconds / (24 * 60 * 60)
    );

    const hours = Math.floor(
      (seconds %
        (24 * 60 * 60)) /
      3600
    );

    const minutes = Math.floor(
      (seconds % 3600) / 60
    );

    const secs = seconds % 60;

    if (days > 0) {

      return `${days}d ${hours}h left`;

    }

    if (hours > 0) {

      return `${hours}h ${minutes}m left`;

    }

    if (minutes > 0) {

      return `${minutes}m ${secs}s left`;

    }

    return `${secs}s left`;

  };

  /* ================= DISCOUNT PRICE ================= */

  const getDiscountedPrice = (
    price,
    offer
  ) => {

    if (!offer) return price;

    const type = (
      offer.offer_type || ""
    ).toLowerCase();

    if (type.includes("percent")) {

      return (
        price -
        (price *
          Number(
            offer.discount_percentage ||
            0
          )) /
        100
      );

    }

    if (type.includes("flat")) {

      return Math.max(
        price -
        Number(
          offer.flat_amount || 0
        ),
        0
      );

    }

    return price;

  };

  /* ================= COUNTRY FLAG ================= */

  const getFlag = (country) => {

    const flags = {
      India: "🇮🇳",
      Qatar: "🇶🇦",
      USA: "🇺🇸",
      UK: "🇬🇧",
      Turkey: "🇹🇷",
      China: "🇨🇳",
      Pakistan: "🇵🇰",
      Italy: "🇮🇹",
    };

    return flags[country] || "🌍";

  };

  /* ================= CART ================= */

  const addItem = (item) => {

    setCart((prev) => ({
      ...prev,
      [item.id]:
        (prev[item.id] || 0) + 1,
    }));

  };

  const removeItem = (item) => {

    setCart((prev) => {

      const updated = { ...prev };

      if (updated[item.id] === 1) {

        delete updated[item.id];

      } else {

        updated[item.id] -= 1;

      }

      return updated;

    });

  };




  /* ================= SLIDER ================= */

  const settings = {

    dots: false,

    arrows: true,

    infinite: deals.length > 5,

    speed: 600,

    slidesToShow: 5,

    slidesToScroll: 1,

    autoplay: true,

    autoplaySpeed: 3000,

    adaptiveHeight: false,

    variableWidth: false,

    centerMode: false,

    responsive: [

      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 5,
        },
      },

      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
        },
      },

      {
        breakpoint: 992,
        settings: {
          slidesToShow: 3,
        },
      },

      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },

      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
        },
      },

    ],

  };

  return (

    <section className="mahal-deals-section">

      <div className="container">

        {/* =========================================
              SECTION HEADER
    ========================================= */}

        <div className="row align-items-end mahal-section-header">

          <div className="col-lg-7 col-md-8">

            <div className="mahal-heading-wrap">

              <span className="mahal-section-tag">



              </span>

              <h2 className="mahal-title15">

                Top <span>Discounts</span>

              </h2>

              <p className="mahal-desc">

                Explore trending supplier offers and exclusive flash discounts.

              </p>

            </div>

          </div>

        </div>

        {/* =========================================
              LOADING
    ========================================= */}

        {loading ? (

          <div className="text-center py-5">

            Loading...

          </div>

        ) : deals.length === 0 ? (

          <div className="text-center text-danger py-5">

            No deals found

          </div>

        ) : (

          <Slider {...settings}>


            {deals.map((p, i) => {

              const originalPrice = Number(

                String(
                  p.price_numeric ||
                  p.price ||
                  0
                ).replace(/[^\d.]/g, "")

              );

              const finalPrice = getDiscountedPrice(
                originalPrice,
                p
              );

              return (

                <div
                  className="mahal-card-slide"
                  key={`${p.id}-${i}`}
                >

                  <article className="mahal-card">

                    <div className="accent-line"></div>

                    {/* ================= IMAGE ================= */}

                    <div className="mahal-card-media">

                      <img
                        src={
                          p.image && p.image.trim() !== ""
                            ? p.image.startsWith("http")
                              ? p.image
                              : `https://api.mahal.qa/${p.image}`
                            : p.img1 && p.img1.trim() !== ""
                              ? p.img1.startsWith("http")
                                ? p.img1
                                : `https://api.mahal.qa/${p.img1}`
                              : "/fallback.png"
                        }
                        alt={p.name}
                        className="mahal-product-image"
                        loading="lazy"
                        draggable={false}
                        onClick={() => {

                          if (!checkAuth()) return;

                          navigate(`/ShopDetails/${p.id}${location.search}`);

                        }}
                        onError={(e) => {

                          e.target.src = "/fallback.png";

                        }}
                      />

                      {/* ================= OFFER TAG ================= */}

                      <div className="mahal-offer-tag">

                        <span>

                          {(() => {

                            const t = (p.offer_type || "").toLowerCase();

                            if (t.includes("percent")) {

                              return `${Math.round(p.discount_percentage)}% OFF`;

                            }

                            if (t.includes("flat")) {

                              return `QAR ${p.flat_amount}`;

                            }

                            if (t.includes("bogo") || t.includes("buy")) {

                              return `BUY ${p.buy_quantity || 1} GET ${p.get_quantity || 1}`;

                            }

                            return "SPECIAL";

                          })()}

                        </span>

                      </div>

                      {/* ================= COUNTRY ================= */}

                      {p.country_of_origin && (

                        <div className="mahal-country-chip">

                          <span className="flag">

                            {getFlag(p.country_of_origin)}

                          </span>

                          <span className="country-name">

                            {p.country_of_origin}

                          </span>

                        </div>

                      )}

                      {/* ================= HOVER ACTIONS ================= */}

                      <div className="mahal-hover-tools">

                        <button
                          type="button"
                          onClick={() => {

                            if (!checkAuth()) return;

                            navigate(`/ShopDetails/${p.id}${location.search}`);

                          }}
                        >

                          <i className="far fa-eye"></i>

                        </button>

                        <button type="button">

                          <i className="far fa-heart"></i>

                        </button>

                      </div>

                    </div>

                    {/* ================= BODY ================= */}

                    <div className="mahal-card-body">

                      <div className="mahal-card-top">

                        {/* ================= PRODUCT HEADER ================= */}

                        <div className="mahal-card-header">

                          <h4
                            className="mahal-card-title"
                            title={p.name}
                          >

                            {p.name}

                          </h4>

                          <div className="mahal-unit-chip">

                            {p.unit_of_measure || "Unit"}

                          </div>

                        </div>

                        {/* ================= PRICE ================= */}

                        <div className="mahal-price-section">

                          <div className="mahal-current-price">

                            <span className="currency">

                              QAR

                            </span>

                            <span className="amount">

                              {finalPrice.toFixed(2)}

                            </span>

                          </div>

                          {finalPrice < originalPrice && (

                            <div className="mahal-price-meta">

                              <div className="mahal-old-price">

                                QAR {originalPrice.toFixed(2)}

                              </div>

                              <div className="mahal-saving">

                                Save {Math.round(
                                  ((originalPrice - finalPrice) / originalPrice) * 100
                                )}%

                              </div>

                            </div>

                          )}

                        </div>

                      </div>

                      {/* ================= FOOTER ================= */}

                      <div className="mahal-card-footer">

                        {!cart[p.id] ? (

                          <button
                            className="mahal-cart-btn"
                            onClick={() => {

                              if (!checkAuth()) return;

                              addItem(p);

                            }}
                          >

                            <i className="fas fa-cart-plus"></i>

                            <span>

                              Add to Cart

                            </span>

                          </button>

                        ) : (

                          <div className="mahal-stepper">

                            <button
                              type="button"
                              className="minus"
                              onClick={() => removeItem(p)}
                            >

                              −

                            </button>

                            <span>

                              {cart[p.id]}

                            </span>

                            <button
                              type="button"
                              className="plus"
                              onClick={() => addItem(p)}
                            >

                              +

                            </button>

                          </div>

                        )}

                      </div>

                    </div>

                  </article>

                </div>

              );
            })}

          </Slider>

        )}

      </div>

    </section>

  );

};

export default RestaurantDeals;